import React, { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import moment from "moment";
import {
  DATE_PRESET,
  getEffectiveReportDateBounds,
} from "../../../pages/report/inventoryReportDateUtils";
import {
  formatCompanyDateTime,
  getCompanyDateTimeLocalValue,
  getCompanyNowDate,
  getCompanyNowParts,
} from "../../../utils/companyDateTime";
import "./InventoryReportPeriodPicker.css";

function formatTriggerRange(start, end, timezoneOptions) {
  if (!start || !end) return "Select date range";
  const startLabel = formatCompanyDateTime(start, timezoneOptions);
  const endLabel = formatCompanyDateTime(end, timezoneOptions);
  if (moment(start).isSame(end, "minute")) return startLabel;
  return `${startLabel} – ${endLabel}`;
}

function formatActiveLine(start, end, timezoneOptions) {
  if (!start || !end) return "";
  return `${formatCompanyDateTime(start, timezoneOptions)} — ${formatCompanyDateTime(end, timezoneOptions)}`;
}

const PANEL_PRESET_OPTIONS = [
  { id: DATE_PRESET.LAST_1, label: "Last 1 day" },
  { id: DATE_PRESET.LAST_2, label: "Last 2 days" },
  { id: DATE_PRESET.LAST_5, label: "Last 5 days" },
  { id: DATE_PRESET.THIS_WEEK, label: "This week" },
  { id: DATE_PRESET.LAST_7, label: "Last 7 days" },
  { id: DATE_PRESET.LAST_30, label: "Last 30 days" },
  { id: DATE_PRESET.START_TO_NOW, label: "From date" },
];

const InventoryReportPeriodPicker = ({
  themeColor,
  dateRangePreset,
  rawStartDate,
  rawEndDate,
  compTimeZone,
  compUTCTimeZone,
  onPresetClick,
  onApplyCustomStart,
  onClearQuick,
  onStartToNowDateChange,
  variant = "default",
}) => {
  const [quickOpen, setQuickOpen] = useState(false);
  const [panelView, setPanelView] = useState("presets");
  const [customStart, setCustomStart] = useState("");
  const [fromDateStart, setFromDateStart] = useState("");
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);

  const DROPDOWN_WIDTH = 300;
  const DROPDOWN_MARGIN = 12;

  const timezoneOptions = useMemo(
    () => ({ compTimeZone, compUTCTimeZone }),
    [compTimeZone, compUTCTimeZone]
  );

  const companyNowLocalMax = useMemo(
    () => getCompanyDateTimeLocalValue(null, timezoneOptions),
    [timezoneOptions]
  );

  const { startDate: effStart, endDate: effEnd } = useMemo(
    () => getEffectiveReportDateBounds(dateRangePreset, rawStartDate, rawEndDate, timezoneOptions),
    [dateRangePreset, rawStartDate, rawEndDate, timezoneOptions]
  );

  const activeLine = useMemo(
    () => formatActiveLine(effStart, effEnd, timezoneOptions),
    [effStart, effEnd, timezoneOptions]
  );
  const triggerText = useMemo(
    () => formatTriggerRange(effStart, effEnd, timezoneOptions),
    [effStart, effEnd, timezoneOptions]
  );

  const companyTodayDate = useMemo(() => {
    const p = getCompanyNowParts(timezoneOptions);
    return `${p.year}-${p.month}-${p.day}`;
  }, [timezoneOptions]);

  const updateDropdownPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const maxWidth = Math.min(DROPDOWN_WIDTH, window.innerWidth - DROPDOWN_MARGIN * 2);
    let left = rect.left;
    if (left + maxWidth > window.innerWidth - DROPDOWN_MARGIN) {
      left = window.innerWidth - maxWidth - DROPDOWN_MARGIN;
    }
    if (left < DROPDOWN_MARGIN) {
      left = DROPDOWN_MARGIN;
    }
    setDropdownPosition({
      top: rect.bottom + 6,
      left,
      width: maxWidth,
    });
  }, []);

  useLayoutEffect(() => {
    if (!quickOpen) {
      setDropdownPosition(null);
      return undefined;
    }
    updateDropdownPosition();
    window.addEventListener("resize", updateDropdownPosition);
    window.addEventListener("scroll", updateDropdownPosition, true);
    return () => {
      window.removeEventListener("resize", updateDropdownPosition);
      window.removeEventListener("scroll", updateDropdownPosition, true);
    };
  }, [quickOpen, panelView, updateDropdownPosition]);

  useEffect(() => {
    const onDoc = (e) => {
      const target = e.target;
      if (
        wrapRef.current?.contains(target) ||
        dropdownRef.current?.contains(target)
      ) {
        return;
      }
      setQuickOpen(false);
      setPanelView("presets");
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const applyPreset = (presetId) => {
    if (presetId === DATE_PRESET.START_TO_NOW) {
      setFromDateStart(
        rawStartDate && moment(rawStartDate).isValid()
          ? moment(rawStartDate).format("YYYY-MM-DD")
          : moment().subtract(29, "day").format("YYYY-MM-DD")
      );
      setPanelView("fromDate");
      return;
    }
    onPresetClick(presetId);
    setQuickOpen(false);
    setPanelView("presets");
  };

  const openCustom = () => {
    setCustomStart(getCompanyDateTimeLocalValue(effStart || null, timezoneOptions));
    setPanelView("custom");
  };

  const applyCustom = () => {
    if (!customStart || !onApplyCustomStart) return;
    onApplyCustomStart(customStart);
    setQuickOpen(false);
    setPanelView("presets");
  };

  const applyFromDate = () => {
    if (!fromDateStart || !onStartToNowDateChange) return;
    onStartToNowDateChange({ target: { value: fromDateStart } });
    setQuickOpen(false);
    setPanelView("presets");
  };

  const handleClear = () => {
    onClearQuick();
    setQuickOpen(false);
    setPanelView("presets");
  };

  const isToolbar = variant === "toolbar";
  const triggerTitle =
    activeLine &&
    `Active range: ${activeLine}. End is always the current company date and time.`;

  const dropdownPanel = quickOpen && dropdownPosition ? (
    <div
      ref={dropdownRef}
      className={`inv-report-quick-select__dropdown inv-report-quick-select__dropdown--portal${isToolbar ? " inv-report-quick-select__dropdown--compact" : ""}`}
      style={{
        position: "fixed",
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
        zIndex: 2147483646,
      }}
    >
      {panelView === "presets" && (
        <>
          <div className="inv-report-quick-select__head">
            <i className="fa fa-clock" aria-hidden />
            Quick Select
          </div>
          <div className="inv-report-quick-select__grid">
            {PANEL_PRESET_OPTIONS.map(({ id, label }) => (
              <button
                key={id}
                type="button"
                className="inv-report-quick-select__preset"
                onClick={() => applyPreset(id)}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="inv-report-quick-select__footer">
            <button type="button" className="inv-report-quick-select__btn-clear" onClick={handleClear}>
              <i className="fa fa-times" aria-hidden />
              Clear
            </button>
            <button type="button" className="inv-report-quick-select__btn-primary" onClick={openCustom}>
              <i className="fa fa-calendar" aria-hidden />
              Custom Date
            </button>
          </div>
        </>
      )}

      {panelView === "fromDate" && (
        <div className="inv-report-quick-select__custom">
          <div className="inv-report-quick-select__head border-0 px-0 pt-0">From date</div>
          <div className="inv-report-quick-select__custom-row">
            <label htmlFor="inv-from-date-start">Start</label>
            <input
              id="inv-from-date-start"
              type="date"
              value={fromDateStart}
              max={companyTodayDate}
              onChange={(e) => setFromDateStart(e.target.value)}
            />
          </div>
          <p className="small text-muted mb-2 px-1">
            End: {formatCompanyDateTime(getCompanyNowDate(), timezoneOptions)} (company time, not editable)
          </p>
          <div className="inv-report-quick-select__custom-actions">
            <button
              type="button"
              className="inv-report-quick-select__btn-clear"
              onClick={() => setPanelView("presets")}
            >
              Back
            </button>
            <button type="button" className="inv-report-quick-select__btn-primary" onClick={applyFromDate}>
              Apply
            </button>
          </div>
        </div>
      )}

      {panelView === "custom" && (
        <div className="inv-report-quick-select__custom">
          <div className="inv-report-quick-select__head border-0 px-0 pt-0">Custom start</div>
          <div className="inv-report-quick-select__custom-row">
            <label htmlFor="inv-quick-start">Start</label>
            <input
              id="inv-quick-start"
              type="datetime-local"
              value={customStart}
              max={companyNowLocalMax}
              onChange={(e) => setCustomStart(e.target.value)}
            />
          </div>
          <p className="small text-muted mb-2 px-1">
            End: {formatCompanyDateTime(getCompanyNowDate(), timezoneOptions)} (company time, not editable)
          </p>
          <div className="inv-report-quick-select__custom-actions">
            <button
              type="button"
              className="inv-report-quick-select__btn-clear"
              onClick={() => setPanelView("presets")}
            >
              Back
            </button>
            <button type="button" className="inv-report-quick-select__btn-primary" onClick={applyCustom}>
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  ) : null;

  return (
    <div
      className={`inv-report-period-picker${isToolbar ? " inv-report-period-picker--toolbar" : " px-3 py-3"}`}
      style={isToolbar ? undefined : { backgroundColor: themeColor }}
    >
      <div className="inv-report-period-picker__header-row">
        {!isToolbar && <h2 className="inv-report-period-picker__title">REPORT PERIOD</h2>}
        <div className="inv-report-quick-select" ref={wrapRef}>
          <button
            ref={triggerRef}
            type="button"
            className={`inv-report-quick-select__trigger ${quickOpen ? "inv-report-quick-select__trigger--open" : ""}`}
            onClick={() => {
              setQuickOpen((o) => !o);
              if (!quickOpen) setPanelView("presets");
            }}
            aria-expanded={quickOpen}
            title={isToolbar ? triggerTitle || undefined : undefined}
          >
            <i className="fa fa-calendar-alt" aria-hidden />
            <span className="text-truncate">{triggerText}</span>
            <i className="fa fa-chevron-down" aria-hidden />
          </button>
        </div>
      </div>

      {typeof document !== "undefined" && dropdownPanel
        ? createPortal(dropdownPanel, document.body)
        : null}

      {!isToolbar && activeLine && (
        <div className="inv-report-period-picker__divider">
          <p className="inv-report-period-picker__active-range mb-0">
            Active range: <span className="fw-semibold">{activeLine}</span>
            <span className="d-block small mt-1" style={{ opacity: 0.9 }}>
              End is always the current company date and time.
            </span>
          </p>
        </div>
      )}
    </div>
  );
};

export default InventoryReportPeriodPicker;
